import * as React from 'react';

import { DocumentType } from '@rematter/frontend-lib-api-client';
import { Checkbox, Col, ContentCard, Row } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';

import { useTranslations } from '../../../i18n/useTranslations';
import { useOutboundPrintDocuments } from '../hooks/useOutboundPrintDocuments';

import { PrintingActionButtons } from '~/modules/trade/_components/_shared/FloatingActionBar';
import { useGenerateDocumentsAndSendEmail } from '~/modules/trade/hooks/useGenerateDocumentsAndSendEmail';
import { usePrintWithBrowser } from '~/modules/trade/hooks/usePrintWithBrowser';
import { LoadRecord } from '~/modules/trade/types/load';

type DocumentSelection = Partial<Record<DocumentType, boolean>>;

type OutboundTicketTabDocumentsProps = {
  ticket: LoadRecord;
};

//
// ⚛️ ⚛️ ⚛️ OUTBOUND TICKET TAB DOCUMENTS ⚛️ ⚛️ ⚛️
//
export const OutboundTicketTabDocuments: React.FC<
  OutboundTicketTabDocumentsProps
> = ({ ticket }) => {
  const t = useTranslations();

  // States
  const [selectedDocuments, setSelectedDocuments] =
    React.useState<DocumentSelection>({});
  const [isDownloading, setIsDownloading] = React.useState(false);

  // Data hooks
  const printableDocuments = useOutboundPrintDocuments(ticket);
  const toast = useToast();
  const { print, isPrinting } = usePrintWithBrowser();
  const { sendDocumentsAndEmail, isPending } =
    useGenerateDocumentsAndSendEmail();

  const selectedCount = Object.values(selectedDocuments).filter(Boolean).length;

  const buildSelectedDocuments = React.useCallback(
    () =>
      printableDocuments
        .filter((doc) => selectedDocuments[doc.documentType])
        .map((doc) => ({
          documentType: doc.documentType,
          loadId: doc.loadId,
          invoiceId: doc.invoiceId,
          salesOrderId: doc.salesOrderId,
        })),
    [printableDocuments, selectedDocuments],
  );

  // Callbacks
  const handleDocumentSelect = React.useCallback(
    (documentType: DocumentType, checked: boolean) => {
      setSelectedDocuments((prev) => ({
        ...prev,
        [documentType]: checked,
      }));
    },
    [],
  );

  const handlePrintDocuments = React.useCallback(async () => {
    await print({ documents: buildSelectedDocuments() });
  }, [buildSelectedDocuments, print]);

  const handleDownloadDocuments = React.useCallback(async () => {
    setIsDownloading(true);
    try {
      await print({ documents: buildSelectedDocuments(), action: 'download' });
    } finally {
      setIsDownloading(false);
    }
  }, [buildSelectedDocuments, print]);

  const handleSendEmail = React.useCallback(
    async (emails: string[]) => {
      if (emails.length === 0) return;

      const documents = buildSelectedDocuments();
      if (documents.length === 0) return;

      const selectedDocumentTypes = documents.map((d) => d.documentType);

      try {
        await sendDocumentsAndEmail({
          documents,
          email: {
            to: emails,
            subject: t.loadDetail.documents.email.subject(ticket.loadNumber),
            textBody: t.loadDetail.documents.email.textBody({
              loadNumber: ticket.loadNumber,
              documentTypes: selectedDocumentTypes.join(', '),
            }),
            htmlBody: t.loadDetail.documents.email.htmlBody({
              loadNumber: ticket.loadNumber,
              documentTypes: selectedDocumentTypes.join(', '),
            }),
          },
        });

        setSelectedDocuments({});
      } catch {
        toast.error(t.documents.email.error);
      }
    },
    [
      buildSelectedDocuments,
      sendDocumentsAndEmail,
      ticket.loadNumber,
      t,
      toast,
    ],
  );

  const handlePostPrint = React.useCallback(() => {
    setSelectedDocuments({});
  }, []);

  return (
    <Col className="@OutboundTicketTabDocuments h-full">
      <ContentCard className="flex h-full flex-1 flex-col p-0">
        <Col xCenter yStart className="flex-1 p-5 text-center">
          <div className="mb-6">
            <h2 className="mb-2 text-lg font-semibold text-neutral">
              {t.scaleTicketPro.documents.title}
            </h2>
            <p className="text-sm text-neutral/80">
              {t.scaleTicketPro.documents.subtitle}
            </p>
          </div>

          <div className="flex flex-col items-center justify-center gap-3 text-center">
            {printableDocuments.map((doc) => {
              const isAvailable = !doc.isDisabled;
              const isSelected = selectedDocuments[doc.documentType];

              return (
                <div
                  key={doc.documentType}
                  className={cn(
                    'flex h-[60px] w-[380px] cursor-pointer items-center gap-3 rounded-md border p-4 transition-colors',
                    'border-neutral/10 outline-2 outline-transparent hover:border-neutral/40',
                    isSelected &&
                      '!border-transparent bg-primary/5 shadow-[0_0_0_2px_var(--color-primary/20)] outline-primary',
                    !isAvailable && 'cursor-not-allowed opacity-50',
                  )}
                  onClick={() =>
                    isAvailable
                      ? handleDocumentSelect(doc.documentType, !isSelected)
                      : undefined
                  }
                >
                  <Checkbox
                    isSelected={isSelected}
                    isDisabled={!isAvailable}
                    className="pointer-events-none"
                  >
                    {doc.label}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </Col>

        <Row gap2 xEnd className="border-t border-neutral/10 p-4">
          <PrintingActionButtons
            selectedCount={selectedCount}
            onPrint={handlePrintDocuments}
            onDownload={handleDownloadDocuments}
            onSendEmail={handleSendEmail}
            onPostPrint={handlePostPrint}
            isPrinting={isPrinting}
            isDownloading={isDownloading}
            isEmailSending={isPending}
          />
        </Row>
      </ContentCard>
    </Col>
  );
};
